import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { requestOTP, resetMpinWithOtp } from '../services/api';

interface ResetMpinLocationState {
  phone?: string;
  tenantCode?: string;
}

const ResetMpin: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const state = (location.state || {}) as ResetMpinLocationState;

  const searchParams = new URLSearchParams(window.location.search);
  const queryTenantCode = searchParams.get('tenantCode') || undefined;

  const [phone, setPhone] = useState(state.phone || '');
  const [tenantCode, setTenantCode] = useState(
    (state.tenantCode || queryTenantCode || '').toUpperCase()
  );
  const [otp, setOtp] = useState('');
  const [newMpin, setNewMpin] = useState('');
  const [confirmMpin, setConfirmMpin] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Auto-focus phone on mount if empty
    const timer = setTimeout(() => {
      const input = document.getElementById('reset-phone') as HTMLInputElement | null;
      if (input && !phone) {
        input.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [phone]);

  const validatePhone = (value: string): string => {
    if (!value) return 'Phone is required';
    const cleaned = value.replace(/[\s\-()]/g, '');
    if (!/^[0-9]{10}$/.test(cleaned)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  const validateTenantCode = (value: string): string => {
    if (!value) return 'Tenant code is required';
    const code = value.toUpperCase();
    if (!/^[A-Z0-9]+$/.test(code)) {
      return 'Tenant code must be letters and numbers only';
    }
    return '';
  };

  const validateMpin = (value: string): string => {
    if (!value) return 'MPIN is required';
    if (!/^[0-9]{4,6}$/.test(value.trim())) {
      return 'MPIN must be 4 to 6 digits';
    }
    return '';
  };

  const handleSendOtp = async () => {
    setError('');
    setSuccess('');

    const phoneError = validatePhone(phone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    const codeError = validateTenantCode(tenantCode);
    if (codeError) {
      setError(codeError);
      return;
    }

    try {
      setLoading(true);
      const codeUpper = tenantCode.toUpperCase();
      await requestOTP(phone, codeUpper);
      setOtpSent(true);
      setSuccess(`OTP sent successfully to ${phone}.`);
      setResendSeconds(60);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to send OTP';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Countdown timer for resend OTP
  useEffect(() => {
    if (!otpSent || resendSeconds <= 0) return;

    const timer = setInterval(() => {
      setResendSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSent, resendSeconds]);

  const handleSaveMpin = async () => {
    setError('');
    setSuccess('');

    const phoneError = validatePhone(phone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    const codeError = validateTenantCode(tenantCode);
    if (codeError) {
      setError(codeError);
      return;
    }

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    const mpinError = validateMpin(newMpin);
    if (mpinError) {
      setError(mpinError);
      return;
    }

    if (newMpin !== confirmMpin) {
      setError('New MPIN and confirm MPIN do not match');
      return;
    }

    try {
      setLoading(true);
      const codeUpper = tenantCode.toUpperCase();
      await resetMpinWithOtp(phone, codeUpper, otp, newMpin);
      setSuccess('MPIN updated successfully. Redirecting you to login...');

      // Small delay so user can read the message
      setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: { phone, tenantCode: codeUpper, loginMethod: 'phone' },
        });
      }, 1000);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to reset MPIN';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    const codeUpper = tenantCode ? tenantCode.toUpperCase() : undefined;
    const target = '/login';

    navigate(target, {
      replace: true,
      state: { phone, tenantCode: codeUpper, loginMethod: 'phone' },
    });
  };

  return (
    <div className="login-container">
      <div className="login-card" style={{ maxWidth: '500px' }}>
        <div className="login-header" style={{ textAlign: 'center', marginBottom: '15px' }}>
          <div
            style={{
              width: 70,
              height: 70,
              borderRadius: 20,
              margin: '0 auto 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(135deg, rgba(56,189,248,1) 0%, rgba(129,140,248,1) 50%, rgba(236,72,153,1) 100%)',
              boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)',
            }}
          >
            <span style={{ fontSize: 32 }}>🔐</span>
          </div>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              marginBottom: 4,
              color: isDark ? '#e5e7eb' : '#0f172a',
            }}
          >
            Reset MPIN
          </h1>
          <p
            style={{
              fontSize: 14,
              color: isDark ? '#9ca3af' : '#64748b',
              maxWidth: 320,
              margin: '0 auto',
            }}
          >
            Verify your phone with OTP and set a new MPIN.
          </p>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${isDark ? '#f97373' : '#fecaca'}`,
              background: isDark ? 'rgba(153,27,27,0.2)' : '#fef2f2',
              color: isDark ? '#fecaca' : '#b91c1c',
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${isDark ? '#4ade80' : '#bbf7d0'}`,
              background: isDark ? 'rgba(22,163,74,0.2)' : '#ecfdf3',
              color: isDark ? '#bbf7d0' : '#166534',
              fontSize: 14,
            }}
          >
            {success}
          </div>
        )}

        {/* Step 1: Phone + Tenant Code (hidden after OTP is sent) */}
        {!otpSent && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 12 }}>
          <div>
            <label
              htmlFor="reset-phone"
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 16,
                fontWeight: 600,
                color: isDark ? '#f1f5f9' : '#0f172a',
              }}
            >
              Phone Number
            </label>
            <input
              id="reset-phone"
              type="tel"
              value={phone}
              maxLength={10}
              onChange={(e) => {
                if (otpSent) return; // lock phone after OTP is sent
                const numeric = e.target.value.replace(/\D/g, '').slice(0, 10);
                setPhone(numeric);
              }}
              placeholder="1234567890"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: `2px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                background: isDark ? '#020617' : 'white',
                color: isDark ? '#e5e7eb' : '#0f172a',
                fontSize: 17,
              }}
            />
          </div>

          <div>
            <label
              htmlFor="reset-tenant"
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 16,
                fontWeight: 600,
                color: isDark ? '#f1f5f9' : '#0f172a',
              }}
            >
              Tenant Code
            </label>
            <input
              id="reset-tenant"
              type="text"
              value={tenantCode}
              onChange={(e) => {
                if (otpSent) return; // lock tenant code after OTP is sent
                setTenantCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
              }}
              placeholder="TENANT"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: `2px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                background: isDark ? '#020617' : 'white',
                color: isDark ? '#e5e7eb' : '#0f172a',
                fontSize: 17,
                textTransform: 'uppercase',
              }}
            />
          </div>
        </div>
        )}

        {/* Step 2: Enter OTP + new MPIN (shown only after OTP is sent) */}
        {otpSent && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          <div>
            <label
              htmlFor="reset-otp"
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 16,
                fontWeight: 600,
                color: isDark ? '#f1f5f9' : '#0f172a',
              }}
            >
              OTP
            </label>
            <input
              id="reset-otp"
              type="text"
              value={otp}
              maxLength={6}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit OTP"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: `2px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                background: isDark ? '#020617' : 'white',
                color: isDark ? '#e5e7eb' : '#0f172a',
                fontSize: 17,
                letterSpacing: 4,
                textAlign: 'center',
              }}
            />
          </div>

          <div style={{ fontSize: 13, color: isDark ? '#9ca3af' : '#6b7280', textAlign: 'right' }}>
            {resendSeconds > 0 ? (
              <span>Resend OTP in {resendSeconds}s</span>
            ) : (
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  color: isDark ? '#a5b4fc' : '#6366f1',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  textDecoration: 'underline',
                  fontSize: 13,
                }}
              >
                Resend OTP
              </button>
            )}
          </div>

          <div>
            <label
              htmlFor="reset-new-mpin"
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 16,
                fontWeight: 600,
                color: isDark ? '#f1f5f9' : '#0f172a',
              }}
            >
              New MPIN
            </label>
            <input
              id="reset-new-mpin"
              type="password"
              value={newMpin}
              maxLength={6}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                setNewMpin(digits);
              }}
              placeholder="4–6 digit MPIN"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: `2px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                background: isDark ? '#020617' : 'white',
                color: isDark ? '#e5e7eb' : '#0f172a',
                fontSize: 17,
              }}
            />
          </div>

          <div>
            <label
              htmlFor="reset-confirm-mpin"
              style={{
                display: 'block',
                marginBottom: 6,
                fontSize: 16,
                fontWeight: 600,
                color: isDark ? '#f1f5f9' : '#0f172a',
              }}
            >
              Confirm New MPIN
            </label>
            <input
              id="reset-confirm-mpin"
              type="password"
              value={confirmMpin}
              maxLength={6}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                setConfirmMpin(digits);
              }}
              placeholder="Re-enter MPIN"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: `2px solid ${isDark ? '#334155' : '#cbd5e1'}`,
                background: isDark ? '#020617' : 'white',
                color: isDark ? '#e5e7eb' : '#0f172a',
                fontSize: 17,
              }}
            />
          </div>
        </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button
            type="button"
            onClick={handleBackToLogin}
            disabled={loading}
            style={{
              flex: 1.4,
              padding: '14px',
              borderRadius: 8,
              border: 'none',
              fontSize: 18,
              fontWeight: 600,
             
              background: loading
              ? '#ccc'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: loading
              ? 'none'
              : '0 4px 15px rgba(102, 126, 234, 0.4)',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Back to Login
          </button>
          <button
            type="button"
            onClick={otpSent ? handleSaveMpin : handleSendOtp}
            disabled={loading || (!otpSent && (!phone || !tenantCode))}
            style={{
              flex: 1.4,
              padding: '14px',
              borderRadius: 8,
              border: 'none',
              fontSize: 18,
              fontWeight: 600,
              cursor: loading || (!otpSent && (!phone || !tenantCode))
                ? 'not-allowed'
                : 'pointer',
              background:
                loading || (!otpSent && (!phone || !tenantCode))
                  ? '#ccc'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow:
                loading || (!otpSent && (!phone || !tenantCode))
                  ? 'none'
                  : '0 4px 15px rgba(102, 126, 234, 0.4)',
            }}
          >
            {loading
              ? otpSent
                ? 'Saving...'
                : 'Sending OTP...'
              : otpSent
                ? 'Save New MPIN'
                : 'Send OTP'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetMpin;

